import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileTypesApi, groupProfilesApi, userProfilesApi } from '../api/client';

// Types
export interface ProfileAttribute {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'select';
  label: string;
  description?: string;
  editable: boolean;
  required: boolean;
  default_value?: unknown;
  options?: string[];
}

export interface ProfileTypeSchema {
  id: string;
  plugin_id: string;
  name: string;
  description: string;
  attributes: ProfileAttribute[];
}

export interface GroupProfile {
  id: string;
  group_id: string;
  profile_type: string;
  attribute_overrides: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AttributeWithSource {
  value: unknown;
  source: 'schema' | 'group' | 'user';
}

export interface EffectiveProfile {
  profile_type: string;
  plugin_id: string;
  name: string;
  description: string;
  attributes: Record<string, AttributeWithSource>;
  sources: string[];
  has_direct: boolean;
}

// Hook for profile types
export function useProfileTypes() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile-types'],
    queryFn: async () => {
      const res = await profileTypesApi.list();
      return res.data?.data as ProfileTypeSchema[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    profileTypes: data || [],
    isLoading,
    error,
  };
}

// Hook for group profiles
export function useGroupProfiles(groupId: string) {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['group-profiles', groupId],
    queryFn: async () => {
      const res = await groupProfilesApi.getByGroup(groupId);
      return res.data?.data as GroupProfile[];
    },
    enabled: !!groupId,
  });

  const linkMutation = useMutation({
    mutationFn: ({ profileType, overrides }: { profileType: string; overrides?: Record<string, unknown> }) =>
      groupProfilesApi.link(groupId, profileType, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-profiles', groupId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ profileType, overrides }: { profileType: string; overrides: Record<string, unknown> }) =>
      groupProfilesApi.updateOverrides(groupId, profileType, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-profiles', groupId] });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (profileType: string) => groupProfilesApi.unlink(groupId, profileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-profiles', groupId] });
    },
  });

  return {
    groupProfiles: data || [],
    isLoading,
    error,
    refetch,
    linkProfile: linkMutation.mutateAsync,
    updateOverrides: updateMutation.mutateAsync,
    unlinkProfile: unlinkMutation.mutateAsync,
    isLinking: linkMutation.isPending,
    isUpdating: updateMutation.isPending,
    isUnlinking: unlinkMutation.isPending,
  };
}

// Hook for user profiles
export function useUserProfiles(userId: string) {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user-profiles', userId],
    queryFn: async () => {
      const res = await userProfilesApi.getByUser(userId);
      return res.data?.data as EffectiveProfile[];
    },
    enabled: !!userId,
  });

  const setMutation = useMutation({
    mutationFn: ({ profileType, attributes }: { profileType: string; attributes?: Record<string, unknown> }) =>
      userProfilesApi.set(userId, profileType, attributes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profiles', userId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ profileType, attributes }: { profileType: string; attributes: Record<string, unknown> }) =>
      userProfilesApi.update(userId, profileType, attributes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profiles', userId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (profileType: string) => userProfilesApi.delete(userId, profileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profiles', userId] });
    },
  });

  return {
    profiles: data || [],
    isLoading,
    error,
    refetch,
    setProfile: setMutation.mutateAsync,
    updateProfile: updateMutation.mutateAsync,
    deleteProfile: deleteMutation.mutateAsync,
    isSetting: setMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Hook for users by profile type
export function useUsersByProfileType(profileType: string, limit = 50, offset = 0) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['profile-users', profileType, limit, offset],
    queryFn: async () => {
      const res = await profileTypesApi.getUsersByType(profileType, { limit, offset });
      return {
        users: res.data?.data || [],
        total: res.data?.total || 0,
      };
    },
    enabled: !!profileType,
  });

  return {
    users: data?.users || [],
    total: data?.total || 0,
    isLoading,
    error,
    refetch,
  };
}
